import axios, { AxiosError } from 'axios';

import config from '../../../config';
import SegmentType from '../interfaces/Segment';
import Segment from '../utils/Segment';
import ClusterConfig from '../interfaces/ClusterConfig';
import { RFMSuggestion } from '../interfaces/RFMSuggestion';
import { HierarchyRFMConfigType } from '../../HierarchyRFMConfig/interfaces/HierarchyRFMConfigType';
import { RFMConfigLevel } from '../interfaces/RFMConfigLevel';

const { apiServerUrl } = config;

export const getRFMConfig = async (
  businessUnitId: number,
  hierarchyLevel?: string,
  abortController?: AbortController
) => {
  return axios
    .get<SegmentType[] | HierarchyRFMConfigType>(
      `${apiServerUrl}/users/rfm-config/${businessUnitId}${
        hierarchyLevel ? `?hierarchyLevel=${hierarchyLevel}` : ''
      }`,
      {
        signal: abortController?.signal
      }
    )
    .then(({ data }) => data)
    .catch((error: AxiosError) => {
      if (error.response?.status === 404) return [];
      throw new Error(error.message);
    });
};

export const updateRFMConfig = (
  businessUnitId: number,
  config:
    | { level: RFMConfigLevel.BUSINESS_UNIT; value: SegmentType[] }
    | { level: RFMConfigLevel.PRODUCT_HIERARCHY; value: HierarchyRFMConfigType }
) => {
  return axios
    .post(`${apiServerUrl}/users/rfm-config/${businessUnitId}`, config)
    .then(({ data }) => data);
};

export const deleteRFMConfig = async (
  businessUnitId: number,
  level: RFMConfigLevel
) => {
  return axios
    .delete(`${apiServerUrl}/users/rfm-config/${businessUnitId}?level=${level}`)
    .then(({ data }) => data);
};

export const getClusterConfig = (businessUnitId: number) => {
  return axios
    .get<ClusterConfig>(
      `${apiServerUrl}/users/rfm-config/${businessUnitId}/cluster-config`
    )
    .then(({ data }) => data)
    .catch((error: AxiosError) => {
      if (error.response?.status === 404) return null;
      throw new Error(error.message);
    });
};

export const updateClusterConfig = (
  businessUnitId: number,
  config: ClusterConfig
) => {
  return axios.post(
    `${apiServerUrl}/users/rfm-config/${businessUnitId}/cluster-config`,
    config
  );
};

export const getRFMSuggestion = (
  businessUnitId: number
): Promise<RFMSuggestion> => {
  return axios
    .get<RFMSuggestion>(
      `${apiServerUrl}/users/analytics-report/${businessUnitId}`
    )
    .then(({ data }) => data)
    .catch((error: AxiosError) => {
      if (error.response?.status === 404) return { data: [], updatedAt: null };
      throw new Error(error.message);
    });
};

export const updateRFMSuggestion = (
  businessUnit: string,
  businessUnitId: number
) => {
  return axios.post(`${apiServerUrl}/users/analytics/rfm`, {
    businessUnit: businessUnit,
    businessUnitId: businessUnitId,
  });
};

export const hasOverlapping = (
  segments: SegmentType[],
  newSegment: SegmentType
) => {
  segments = segments.filter((segment) => segment.id !== newSegment.id); // Exclude the segment being updated
  for (const segment of segments) {
    const isOverlapping = new Segment(segment).hasNoOverlapping(newSegment);
    if (isOverlapping) return segment;
  }
  return null;
};
